// passiveAssetResolver.js
// Resolves raw IoT sensor identifiers (reader IMEI, beacon MAC / BLE id, RFID
// tag) to the linked AssetPro asset so the UI can show asset code, owner code,
// and alt name instead of opaque ids.
//
// Mapping contract (mirrors the Asset Tagging Flutter app):
//   - A GPS/reader device's IMEI is stored on an asset as `tagDeviceId`.
//   - An RFID tag is stored as `rfidTagId`, and/or inside `scannerData[].tagNumber`
//     and/or `rfidLinked` (array or comma-joined string).
//   - A BLE beacon id is stored as `bleTagId`.
// There is no "lookup by sensor" endpoint, so we fetch all assets once via
// GET /asset/asset-all and build a client-side index.

import { apiGet } from './apiService';

// Exact match key: trim only. We deliberately do NOT lowercase or strip
// separators — a sensor id must match the asset's stored id exactly, so we
// never produce a "similar" (and possibly wrong) link.
const keyOf = (v) =>
  String(v === null || v === undefined ? '' : v).trim();

const extractAssets = (res) => {
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  if (d && Array.isArray(d.assets)) return d.assets;
  if (d && Array.isArray(d.results)) return d.results;
  if (d && Array.isArray(d.docs)) return d.docs;
  if (d && Array.isArray(d.rows)) return d.rows;
  return [];
};

const ownerCodeOf = (a) => {
  if (a.ownerCode) return a.ownerCode;
  if (a.owner_code) return a.owner_code;
  const owner = a.owner || a.assetOwner || a.custodian;
  if (owner && typeof owner === 'object') return owner.code || owner.employeeCode || null;
  return null;
};

const idOf = (ref) => {
  if (!ref) return null;
  if (typeof ref === 'string') return ref;
  if (typeof ref === 'object') return ref._id || ref.id || null;
  return null;
};

// The id of an asset's parent/host, across the field shapes this API uses.
const parentIdOf = (a) =>
  idOf(a.parent) || idOf(a.parentId) || idOf(a.parentAsset) || idOf(a.host) || null;

// Build the display info for the HOST asset that a sensor is physically
// attached to. `host` is the resolved parent equipment; `device` is the child
// asset that actually carried the sensor id (may equal host for directly-tagged
// masters). We surface the host's code/owner/alt name, and keep the device code
// as `viaDeviceCode` for traceability.
const toAssetInfo = (host, device) => {
  const h = host || device;
  const info = {
    assetId: h._id || h.id || null,
    assetCode: h.assetCode || h.code || null,
    altName: h.altName || null,
    ownerCode: ownerCodeOf(h),
    displayName: h.altName || h.assetCode || h.code || null,
    raw: h,
    viaDeviceCode: null
  };
  if (device && device !== h) {
    const devCode = device.assetCode || device.code || null;
    if (devCode && devCode !== info.assetCode) info.viaDeviceCode = devCode;
  }
  return info;
};

const readScannerTagNumbers = (scannerData) => {
  let arr = scannerData;
  if (typeof arr === 'string') {
    try { arr = JSON.parse(arr); } catch (e) { arr = []; }
  }
  if (!Array.isArray(arr)) return [];
  return arr
    .map((e) => (e && (e.tagNumber || e.tag_number || e.code || e.number)) || null)
    .filter(Boolean);
};

const readRfidLinked = (rfidLinked) => {
  if (Array.isArray(rfidLinked)) return rfidLinked.filter(Boolean);
  if (typeof rfidLinked === 'string') {
    return rfidLinked.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

// Fetch all assets and build the sensor index. Returns an object with
// resolveReader / resolveTag / resolveAny lookups plus a count.
export async function loadAssetSensorIndex() {
  // /asset/asset-all needs Accept: */* (no Content-Type) in this app.
  // Paginate so large orgs (where BLE child-device records may sit beyond the
  // first page) are fully indexed.
  const PAGE = 5000;
  const MAX_PAGES = 20; // safety cap (100k assets)
  const assets = [];
  let firstError = null;
  for (let p = 0; p < MAX_PAGES; p += 1) {
    const offset = p * PAGE;
    // eslint-disable-next-line no-await-in-loop
    const res = await apiGet(`/asset/asset-all?limit=${PAGE}&offset=${offset}`, {
      headers: { Accept: '*/*' }
    });
    if (res && res.success === false) {
      if (p === 0) throw new Error(res.error || 'Failed to load asset list');
      firstError = res.error;
      break;
    }
    const page = extractAssets(res);
    assets.push(...page);
    if (page.length < PAGE) break; // last page reached
  }
  if (firstError) {
    // Partial load — keep what we have but it's noted via stats.
    console.warn('[passiveAssetResolver] partial asset load:', firstError);
  }

  // Index every asset by its _id so we can climb the parent chain.
  const byId = new Map();
  assets.forEach((a) => {
    const id = a._id || a.id;
    if (id) byId.set(String(id), a);
  });

  // Climb parent links to the top-most host equipment the device is attached
  // to. Guards against missing links and cycles.
  const resolveToHost = (a) => {
    let current = a;
    const seen = new Set();
    for (let i = 0; i < 6; i += 1) {
      const pid = parentIdOf(current);
      if (!pid) break;
      const key = String(pid);
      if (seen.has(key)) break;
      seen.add(key);
      const parent = byId.get(key);
      if (!parent) break; // parent not in list — keep current as best known host
      current = parent;
    }
    return current;
  };

  const byReaderImei = new Map(); // tagDeviceId -> host equipment (exact)
  const byTag = new Map();        // rfidTagId / bleTagId / scannerData / rfidLinked -> host (exact)

  // Diagnostics so the UI can show what was actually indexed (helps tell a
  // data-fetch gap from a genuine linkage gap).
  const stats = { assets: assets.length, withBle: 0, withRfid: 0, withTagDevice: 0, withScanner: 0, withRfidLinked: 0, withDependents: 0 };

  const put = (map, id, info) => {
    const k = keyOf(id);
    if (k && !map.has(k)) map.set(k, info);
  };

  // Index a single device record's sensor ids, mapping them to the HOST asset
  // (parent equipment via parent-child relation) rather than the device child
  // itself. Exact field names only — no field-name guessing.
  const indexDevice = (device, hostHint) => {
    const host = hostHint || resolveToHost(device);
    const info = toAssetInfo(host, device);

    if (device.tagDeviceId) {
      stats.withTagDevice += 1;
      put(byReaderImei, device.tagDeviceId, info);
    }
    if (device.rfidTagId) {
      stats.withRfid += 1;
      put(byTag, device.rfidTagId, info);
    }
    if (device.bleTagId) {
      stats.withBle += 1;
      put(byTag, device.bleTagId, info);
    }
    // Directly-tagged masters: scannerData / rfidLinked live on the host itself,
    // so the "device" and host are the same record (no via).
    const scanTags = readScannerTagNumbers(device.scannerData);
    if (scanTags.length) stats.withScanner += 1;
    scanTags.forEach((t) => put(byTag, t, toAssetInfo(device)));
    const rfidLinks = readRfidLinked(device.rfidLinked);
    if (rfidLinks.length) stats.withRfidLinked += 1;
    rfidLinks.forEach((t) => put(byTag, t, toAssetInfo(device)));
  };

  assets.forEach((a) => {
    // Index the asset itself (climbs to host if it is a child device).
    indexDevice(a);
    // Also index any nested child devices, mapping them to this asset as host.
    if (Array.isArray(a.dependents) && a.dependents.length) {
      stats.withDependents += 1;
      a.dependents.forEach((dep) => {
        if (dep && typeof dep === 'object') indexDevice(dep, a);
      });
    }
  });

  return {
    count: assets.length,
    stats,
    // Exact, field-scoped lookups only: a reader IMEI is matched against reader
    // ids, a tag id against tag ids — never cross-field.
    resolveReader: (imei) => byReaderImei.get(keyOf(imei)) || null,
    resolveTag: (id) => byTag.get(keyOf(id)) || null,
    resolveAny: (id) => byTag.get(keyOf(id)) || byReaderImei.get(keyOf(id)) || null
  };
}
